/**
 * Shared boundary geometry + styling helpers. One source of truth for
 * converting boundary GeoJSON to Leaflet polygons and for the boundary
 * colour palette, used by both `BoundariesOverlay` (general maps) and the
 * FPA Hub's `BoundariesTab` (draw/edit) so they can't drift apart.
 */

export interface BoundaryGeometry {
  type?: string;
  coordinates?: unknown;
}

export interface RenderableBoundaryPolygon {
  key: string;
  positions: [number, number][]; // [lat, lng]
}

export interface BoundaryPathStyle {
  color: string;
  fillColor: string;
  fillOpacity: number;
  weight: number;
  dashArray?: string;
}

// Semantic colours: primary boundary in warm orange, others in muted purple.
export const BOUNDARY_STYLE_PRIMARY: BoundaryPathStyle = {
  color: '#e64a19',
  fillColor: '#ff7d47',
  fillOpacity: 0.08,
  weight: 3,
};

export const BOUNDARY_STYLE_SECONDARY: BoundaryPathStyle = {
  color: '#5e35b1',
  fillColor: '#9575cd',
  fillOpacity: 0.08,
  weight: 2,
  dashArray: '6 6',
};

// Distinct per-boundary palette for the "indexed" colour mode (admin parcels
// map). First colour stays orange to match historical screenshots/docs.
export const BOUNDARY_PALETTE = [
  '#ff9800', '#9c27b0', '#00897b', '#e53935', '#3949ab', '#00acc1', '#5d4037',
];

export const colorForBoundaryIndex = (idx: number): string =>
  BOUNDARY_PALETTE[idx % BOUNDARY_PALETTE.length] as string;

/** Boundary path style by primary/secondary semantics, with overridable opacity. */
export function boundaryStyle(
  isPrimary: boolean,
  opts?: { fillOpacity?: number },
): BoundaryPathStyle {
  const base = isPrimary ? BOUNDARY_STYLE_PRIMARY : BOUNDARY_STYLE_SECONDARY;
  return opts?.fillOpacity != null ? { ...base, fillOpacity: opts.fillOpacity } : base;
}

/** Indexed (distinct-colour) path style — dashed outline, light fill. */
export function indexedBoundaryStyle(
  idx: number,
  opts?: { fillOpacity?: number },
): BoundaryPathStyle {
  const c = colorForBoundaryIndex(idx);
  return {
    color: c,
    fillColor: c,
    fillOpacity: opts?.fillOpacity ?? 0.05,
    weight: 3,
    dashArray: '8 4',
  };
}

/**
 * Convert a boundary GeoJSON Polygon/MultiPolygon to Leaflet [lat, lng] rings.
 * `keyPrefix` namespaces the per-ring keys (e.g. the boundary id).
 */
export function geometryToPolygons(
  geometry: BoundaryGeometry | null | undefined,
  keyPrefix: string | number = 'b',
): RenderableBoundaryPolygon[] {
  if (!geometry || !geometry.coordinates) return [];
  if (geometry.type === 'Polygon') {
    const ring = (geometry.coordinates as [number, number][][])[0] ?? [];
    return [
      {
        key: `${keyPrefix}`,
        positions: ring.map(([lng, lat]) => [lat, lng] as [number, number]),
      },
    ];
  }
  if (geometry.type === 'MultiPolygon') {
    const polys = (geometry.coordinates as [number, number][][][]) ?? [];
    return polys.map((rings, idx) => ({
      key: `${keyPrefix}-${idx}`,
      positions: (rings?.[0] ?? []).map(([lng, lat]) => [lat, lng] as [number, number]),
    }));
  }
  return [];
}

/** `[[south, west], [north, east]]` — the shape Leaflet's `fitBounds` takes. */
export type LatLngBounds = [[number, number], [number, number]];

/**
 * Bounding box over every vertex of the given rings. Returns `null` when
 * there is nothing with coordinates to measure — callers treat that as
 * "no geometry, fall back".
 */
export function boundsFromPolygons(
  polys: RenderableBoundaryPolygon[] | null | undefined,
): LatLngBounds | null {
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  let found = false;

  polys?.forEach((poly) => {
    poly?.positions?.forEach(([lat, lng]) => {
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      if (lat < south) south = lat;
      if (lat > north) north = lat;
      if (lng < west) west = lng;
      if (lng > east) east = lng;
      found = true;
    });
  });

  return found ? [[south, west], [north, east]] : null;
}

/** Centre point of a bounding box. */
export function centerOfBounds(bounds: LatLngBounds): [number, number] {
  const [[south, west], [north, east]] = bounds;
  return [(south + north) / 2, (west + east) / 2];
}

const TILE_SIZE = 256;

/**
 * Web-Mercator zoom level at which `bounds` fits inside `viewport`.
 *
 * This is only used to seed a map's INITIAL zoom before it has mounted (when
 * the real container size is still unknown) — `FitBoundsToData` corrects it
 * to the exact fit on mount. The default viewport is a rough desktop map
 * panel; being a level out just means a barely-visible correction rather than
 * a pan across the country.
 */
export function zoomForBounds(
  bounds: LatLngBounds,
  viewport: { width: number; height: number } = { width: 900, height: 600 },
): number {
  const [[south, west], [north, east]] = bounds;

  const latRad = (lat: number): number => {
    const s = Math.sin((lat * Math.PI) / 180);
    // Clamp keeps the log finite at the poles.
    return Math.log((1 + s) / (1 - s)) / 2;
  };

  const lngFraction = Math.abs(east - west) / 360;
  const latFraction = Math.abs(latRad(north) - latRad(south)) / (2 * Math.PI);

  const zoomFor = (fraction: number, px: number): number =>
    fraction > 0 ? Math.log2(px / TILE_SIZE / fraction) : Infinity;

  const zoom = Math.min(
    zoomFor(lngFraction, viewport.width),
    zoomFor(latFraction, viewport.height),
  );

  if (!Number.isFinite(zoom)) return 13; // degenerate bbox (a point)
  // Floor so the box is never cropped; clamp to the range a boundary can
  // plausibly need — a country-sized FPA at 5, a smallholding cluster at 15.
  return Math.max(5, Math.min(15, Math.floor(zoom)));
}
